import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PricingCard from "@/components/PricingCard";
import { SectionHeader } from "@/components/SectionHeader";
import {
  agentPackages,
  agencyPackages,
  perAgentAddOns,
} from "@/data/packages";

export default function Packages() {
  return (
    <div>
      {/* Hero */}
      <section
        data-testid="packages-hero"
        className="relative pt-16 pb-20 md:pt-24 md:pb-28 border-b border-[#27272a]"
      >
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow mb-6">
              <span className="gold-line" />
              Packages
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
              Clear packages. <br />
              <span className="text-[#C8A24B]">Serious presence.</span>
            </h1>
            <p className="mt-8 text-lg text-[#a1a1aa] leading-relaxed max-w-2xl">
              Three tiers for individual agents. Three tiers for agencies.
              Every plan is built to grow with the business, so you can start
              where you are and upgrade when it makes sense.
            </p>
          </div>
        </div>
      </section>

      {/* Agent packages */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal">
            <SectionHeader
              eyebrow="For Individual Agents"
              title="Foundation, Growth, Managed."
              subtitle="Start with a professional foundation. Add ongoing visibility and lead generation. Move to fully managed when you want Agent Marketing to run the operation."
            />
          </div>
          <div className="reveal mt-16 grid lg:grid-cols-3 gap-6 lg:gap-8">
            {agentPackages.map((pkg, i) => (
              <PricingCard key={pkg.id} pkg={pkg} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Agency packages */}
      <section className="py-24 md:py-32 border-t border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal">
            <SectionHeader
              eyebrow="For Agencies & Brokerages"
              title="Alignment, Growth, Authority."
              subtitle="Presence systems for agencies, brokerages, recruiting firms, mortgage teams, and insurance offices that need a consistent brand across the organization."
            />
          </div>
          <div className="reveal mt-16 grid lg:grid-cols-3 gap-6 lg:gap-8">
            {agencyPackages.map((pkg, i) => (
              <PricingCard key={pkg.id} pkg={pkg} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Per-agent add-ons */}
      <section className="py-24 md:py-32 border-t border-[#27272a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal grid lg:grid-cols-[1fr_1.1fr] gap-14 items-start">
            <SectionHeader
              eyebrow="Agent Alignment Add-Ons"
              title="Keep every agent aligned with the parent brand."
              subtitle="Every agent inside an agency affects the brand. Agent Marketing creates per-agent systems that keep each professional aligned with the parent brand while still allowing individual personality and market focus."
            />
            <div>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {perAgentAddOns.map((a) => (
                  <div
                    key={a}
                    className="flex items-start gap-3 border border-[#27272a] px-4 py-3 rounded-sm"
                  >
                    <Check size={14} className="text-[#C8A24B] flex-shrink-0 mt-1" />
                    <span className="text-sm text-[#d4d4d8]">{a}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 border border-[#C8A24B]/40 p-6 rounded-sm bg-[#C8A24B]/[0.03]">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-2">
                  Ongoing Support Starts From
                </div>
                <div className="font-heading text-3xl text-white">
                  $49<span className="text-lg text-[#a1a1aa] font-body font-normal">/month per agent</span>
                </div>
                <div className="text-sm text-[#a1a1aa] mt-2">
                  Setup quoted based on volume, assets, and page structure.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment terms */}
      <section className="py-24 md:py-32 border-t border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal grid md:grid-cols-2 gap-14 items-start">
            <SectionHeader
              eyebrow="Payment Terms"
              title="Straightforward, transparent, professional."
            />
            <ul className="space-y-3 text-[#d4d4d8]">
              {[
                "Setup fees are due before work begins unless otherwise stated.",
                "Monthly fees are billed at the start of each service period.",
                "Custom projects may require 50% upfront and 50% before launch.",
                "No final website or asset release until required payment is received.",
                "Ongoing plans require active payment status.",
                "Approved terms may be considered only for established agency accounts.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 border-l border-[#C8A24B]/40 pl-4 py-1">
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal mt-14 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-gold">
              Build My Growth Plan <ArrowRight size={16} />
            </Link>
            <Link to="/faq" className="btn-outline">
              Read the FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
